/**
 * "There is a newer version. Refresh."
 *
 * ── WHY THIS IS NEEDED ──────────────────────────────────────────────────────
 *
 * This is a single page application. Once the browser has the bundle it keeps
 * running it, and nothing makes it ask for a newer one: a tab left open on
 * Friday is still running Friday's code on Monday. The person using it has no
 * way to know, and neither does anybody supporting them.
 *
 * It matters more here than on a website, because the two halves disagree. The
 * server is new after a deploy and the tab is old, so a screen can ask for a
 * route that has moved or send a field the handler has stopped reading. The
 * symptom is one person, on one tab, seeing something nobody can reproduce.
 *
 * ── HOW THE VERSION IS KNOWN, WITHOUT A BUILD STEP ──────────────────────────
 *
 * There is no version number anywhere, and adding one means remembering to
 * change it, which is the class of thing that goes stale on the third deploy.
 *
 * There is already a perfect signal. The build writes hashed file names, so
 * the shell says `<script src="/chunk-jjr92qgr.js">`, and that name changes
 * when, and only when, the client code changes. The shell itself is served
 * `no-cache, must-revalidate`, which `scripts/deploy.sh` checks on every
 * deploy, so asking for it again always gets the current one.
 *
 * So: read the chunk name THIS TAB IS RUNNING out of its own DOM, ask the
 * server for the shell, read the chunk name it serves now, and compare. No
 * build plumbing, no version to bump, no endpoint to keep in step, and it
 * compares the bundle actually running to the bundle actually served rather
 * than two numbers that are supposed to correspond to them.
 *
 * A deploy that changes only server code leaves the chunk alone and shows
 * nothing. That is correct: there is nothing for the tab to refresh.
 *
 * ── WHAT IT DOES NOT DO ─────────────────────────────────────────────────────
 *
 * IT NEVER RELOADS THE PAGE BY ITSELF. Somebody is half way through typing an
 * order. Reloading under them would lose it, and would do so at the exact
 * moment they are most annoyed about it. It offers, they choose, and they can
 * dismiss it and carry on.
 */
import { useEffect, useRef, useState } from 'react';

/** How often to ask. Long enough to be free, short enough to be useful. */
const EVERY_MS = 5 * 60 * 1000;

/** Don't ask more than this often, however many times the tab is focused. */
const AT_MOST_EVERY_MS = 60 * 1000;

/**
 * The module chunk named by a piece of shell HTML.
 *
 * The same regex reads this tab's own document and the server's answer, so the
 * two can only disagree about the value and never about how it was found.
 */
function chunkIn(html: string): string | null {
  return /<script[^>]+src="([^"]*\/chunk-[^"]+\.js)"/.exec(html)?.[1] ?? null;
}

/** What this tab is running, from the DOM rather than from a copy of the HTML. */
function runningChunk(): string | null {
  if (typeof document === 'undefined') return null;
  const tags = [...document.querySelectorAll<HTMLScriptElement>('script[src]')];
  const chunk = tags.map((t) => t.getAttribute('src') ?? '')
    .find((src) => /\/chunk-[^/]+\.js$/.test(src));
  return chunk ?? null;
}

export function NewVersion() {
  const [stale, setStale] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const mine = useRef<string | null>(null);
  const lastAsked = useRef(0);

  useEffect(() => {
    mine.current = runningChunk();
    // Nothing to compare against. In development there is no hashed chunk, and
    // a check that cannot work must do nothing rather than guess.
    if (!mine.current) return;

    let alive = true;

    const check = async () => {
      if (Date.now() - lastAsked.current < AT_MOST_EVERY_MS) return;
      lastAsked.current = Date.now();
      try {
        const res = await fetch('/', {
          cache: 'no-store',
          credentials: 'same-origin',
          headers: { accept: 'text/html' },
        });
        if (!res.ok || !alive) return;
        const served = chunkIn(await res.text());
        // A shell with no chunk in it is an error page or a proxy's idea of
        // one. Saying "there is a new version" because a request went wrong
        // would train people to ignore the bar.
        if (served && served !== mine.current) setStale(true);
      } catch {
        // Offline, or the box is restarting mid deploy. Neither is news.
      }
    };

    const onVisible = () => { if (document.visibilityState === 'visible') void check(); };

    const timer = setInterval(() => { void check(); }, EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  if (!stale || dismissed) return null;

  return (
    <div
      // `status`, not `alert`. A screen reader announces it at the next pause
      // rather than interrupting what the person is doing, which is the same
      // decision as not reloading the page.
      role="status"
      className="fixed inset-x-0 bottom-0 z-[60] flex flex-wrap items-center justify-center gap-3 border-t border-line bg-panel px-4 py-3 shadow-[0_-2px_12px_rgba(0,0,0,0.08)]"
    >
      <p className="text-[13px]">
        <span className="font-semibold">A newer version has been released.</span>{' '}
        <span className="text-muted">
          This tab is still running the old one. Refresh when you are ready.
        </span>
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-[6px] bg-[color:var(--color-control)] px-3 py-1.5 text-[13px] font-semibold text-[color:var(--color-on-control)] hover:bg-[color:var(--color-control-hover)]"
        >
          Refresh now
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="rounded-[6px] px-3 py-1.5 text-[13px] font-semibold text-muted hover:bg-raised hover:text-ink"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
